import moment from "moment";

export const getAvailablePreorderDates = (preorderIntervals) => {
    return preorderIntervals?.data ?
        Object.keys(preorderIntervals?.data).filter(i => parseInt(i)).map(i => moment(i, "YYYY-MM-DD"))
        : []
}

export const getMinDate = (preorderIntervals) => {
    if (!preorderIntervals) return null;
    const availablePreorderDate = getAvailableDay(preorderIntervals);
    return new Date(moment(availablePreorderDate).format());
}

export const getMaxDate = (preorderIntervals) => {
    if (!preorderIntervals) return null;
    const availablePreorderDates = getAvailablePreorderDates(preorderIntervals);
    return new Date(moment.max(availablePreorderDates).format());
}

const getAvailableDay = (preorderIntervals, dateString) => {
    for (const day in preorderIntervals.data) {
        if (!['disabledDeliveryDays', 'disabledPickupDays', 'protected_at', 'ttl'].includes(day)) {
            for (const hours in preorderIntervals.data[day]) {
                for (const minutes in preorderIntervals.data[day][hours]) {
                    if (preorderIntervals.data[day][hours][minutes].delivery) {
                        return day
                    }
                }
            }
        }
    }

    return dateString
}

export const getFirstAvailableDayTime = (preorderIntervals) => {
    for (const day in preorderIntervals?.data) {
        if (!['disabledDeliveryDays', 'disabledPickupDays', 'protected_at', 'ttl'].includes(day)) {
            for (const hours in preorderIntervals?.data[day]) {
                for (const minutes in preorderIntervals?.data[day][hours]) {
                    if (preorderIntervals?.data?.[day][hours][minutes].delivery) {
                        return new Date((day + ' ' + hours + ':' + minutes))
                    }
                }
            }
        }
    }

    return false
}

const getAvailableHoursForTheDay = (preorderIntervals, dateString, isDelivery) => {
    const excludedKeys = ['disabledDeliveryHours', 'disabledPickupHours', 'isDayAvailableForDelivery', 'isDayAvailableForPickup'];
    
    return Object.keys(preorderIntervals?.data?.[dateString] || {}).filter(a => {
        if (excludedKeys.includes(a) || isNaN(parseInt(a)) || parseInt(a) < 0) {
            return false;
        }
        
        for (const minutes in preorderIntervals?.data?.[dateString][a]) {
            const timeSlot = preorderIntervals?.data?.[dateString][a][minutes];
            if (timeSlot && timeSlot[isDelivery ? 'delivery' : 'pickup'] && timeSlot.is_allowed) {
                return true;
            }
        }

        return false;
    }).map(a => +a)
}

const getAvailableMinutesForTheHour = (preorderIntervals, dateString, hourForADay, isDelivery) => {
    const hourKey = hourForADay.toString().padStart(2, "0");
    const minutes = [];
    
    for (const minuteKey in preorderIntervals?.data?.[dateString]?.[hourKey] || {}) {
        const timeSlot = preorderIntervals?.data?.[dateString]?.[hourKey]?.[minuteKey];
        if (timeSlot && timeSlot[isDelivery ? 'delivery' : 'pickup'] && timeSlot.is_allowed) {
            minutes.push(+minuteKey);
        }
    }
    
    return minutes;
}

export const getMinTimeOfDay = (dayTimeString, preorderIntervals, isDelivery) => {
    if (!preorderIntervals) return new Date(moment(dayTimeString).set('hour', 0).set('minutes', 0).format());

    const dateString = moment(dayTimeString).format("YYYY-MM-DD")
    const availableHours = getAvailableHoursForTheDay(preorderIntervals, dateString, isDelivery);
    
    if (availableHours.length === 0) {
        return new Date(moment(dayTimeString).set('hour', 0).set('minutes', 0).format());
    }
    
    const minHourForADay = Math.min(...availableHours);
    const availableMinutes = getAvailableMinutesForTheHour(preorderIntervals, dateString, minHourForADay, isDelivery);
    
    if (availableMinutes.length === 0) {
        return new Date(moment(dayTimeString).set('hour', minHourForADay).set('minutes', 0).format());
    }
    
    const minMinuteOfTheHour = Math.min(...availableMinutes);

    return new Date(moment(dayTimeString)
        .set('hour', minHourForADay)
        .set('minutes', minMinuteOfTheHour)
        .format()
    );
}

export const getMaxTimeOfDay = (dayTimeString, preorderIntervals, isDelivery) => {
    if (!preorderIntervals) return new Date(moment(dayTimeString).set('hour', 23).set('minutes', 59).format());

    const dateString = moment(dayTimeString).format("YYYY-MM-DD")
    const availableHours = getAvailableHoursForTheDay(preorderIntervals, dateString, isDelivery);
    
    if (availableHours.length === 0) {
        return new Date(moment(dayTimeString).set('hour', 23).set('minutes', 59).format());
    }
    
    const maxHourForADay = Math.max(...availableHours);
    const availableMinutes = getAvailableMinutesForTheHour(preorderIntervals, dateString, maxHourForADay, isDelivery);
    
    if (availableMinutes.length === 0) {
        return new Date(moment(dayTimeString).set('hour', maxHourForADay).set('minutes', 59).format());
    }
    
    const maxMinuteOfTheHour = Math.max(...availableMinutes);

    return new Date(moment(dayTimeString)
        .set('hour', maxHourForADay)
        .set('minutes', maxMinuteOfTheHour)
        .format()
    );
}

export const getFirstAvailableTimeForDay = (dateTime, preorderIntervals, isDelivery) => {
    if (!preorderIntervals) return null;

    const dateString = moment(dateTime).format("YYYY-MM-DD");
    const dayData = preorderIntervals?.data?.[dateString];
    
    if (!dayData) return null;

    const excludedKeys = ['disabledDeliveryHours', 'disabledPickupHours', 'isDayAvailableForDelivery', 'isDayAvailableForPickup'];
    
    for (const hours in dayData) {
        if (!excludedKeys.includes(hours) && !isNaN(parseInt(hours)) && parseInt(hours) >= 0) {
            for (const minutes in dayData[hours]) {
                const timeSlot = dayData[hours][minutes];
                if (timeSlot && timeSlot[isDelivery ? 'delivery' : 'pickup'] && timeSlot.is_allowed) {
                    const timeString = `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
                    return new Date(moment(`${dateString} ${timeString}`, "YYYY-MM-DD HH:mm").format());
                }
            }
        }
    }

    return null;
}

export const getDaysWithoutAvailableIntervals = (preorderIntervals, isDelivery) => {
    if (!preorderIntervals?.data) return [];

    const daysWithoutIntervals = [];
    const excludedKeys = ['disabledDeliveryDays', 'disabledPickupDays', 'protected_at', 'ttl'];
    const timeSlotExcludedKeys = ['disabledDeliveryHours', 'disabledPickupHours', 'isDayAvailableForDelivery', 'isDayAvailableForPickup'];

    for (const dayKey in preorderIntervals.data) {
        if (!excludedKeys.includes(dayKey)) {
            const dayData = preorderIntervals.data[dayKey];
            let hasAvailableTime = false;

            for (const hours in dayData) {
                if (!timeSlotExcludedKeys.includes(hours) && !isNaN(parseInt(hours)) && parseInt(hours) >= 0) {
                    for (const minutes in dayData[hours]) {
                        const timeSlot = dayData[hours][minutes];
                        if (timeSlot && timeSlot[isDelivery ? 'delivery' : 'pickup'] && timeSlot.is_allowed) {
                            hasAvailableTime = true;
                            break;
                        }
                    }
                    if (hasAvailableTime) break;
                }
            }

            if (!hasAvailableTime) {
                daysWithoutIntervals.push(moment(dayKey, "YYYY-MM-DD").toDate());
            }
        }
    }

    return daysWithoutIntervals;
}
